// Photo AI Scanner - Extract product info from images
// Uses Google Cloud Vision API (free tier: 1000/month)

/**
 * Extract text from an image using Google Cloud Vision API
 * @param {File} imageFile - Image file to scan
 * @returns {Promise<Object>} - Extracted data
 */
export async function scanProductPhoto(imageFile) {
  try {
    // For now, we'll use a simpler approach without API key
    // Later, you can add Google Cloud Vision API
    // const base64Image = await fileToBase64(imageFile);
    
    // Mock extraction for demo (we'll enhance with real API)
    const mockData = await mockPhotoScan(imageFile);
    
    return mockData;
  } catch (error) {
    console.error('Photo scan error:', error);
    throw error;
  }
}

/**
 * Convert file to base64
 * @param {File} file - File to convert
 * @returns {Promise<string>} - Base64 string
 * Currently unused but kept for future Google Vision API integration
 */
// eslint-disable-next-line no-unused-vars
function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Mock photo scanning (will be replaced with real API)
 * This demonstrates the structure - you'll replace with Google Vision API
 * @param {File} imageFile - Image file
 * @returns {Promise<Object>} - Extracted data
 */
async function mockPhotoScan(imageFile) {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 1500));
  
  // In production, this would call Google Cloud Vision API
  // For now, return a sample structure
  
  return {
    success: true,
    productName: null, // Will be filled by AI
    price: null, // Will be filled by AI
    store: 'unknown', // 'amazon', 'walmart', or 'unknown'
    asin: null, // Amazon product ID
    detectedText: [], // All text found in image
    confidence: 'medium',
    needsManualReview: true // User should verify
  };
}

/**
 * Detect store from image (logo detection)
 * @param {string} text - Extracted text from image
 * @returns {string} - 'amazon', 'walmart', or 'unknown'
 */
export function detectStore(text) {
  const lowerText = text.toLowerCase();
  
  // Amazon detection
  if (lowerText.includes('amazon') || 
      lowerText.includes('prime') || 
      lowerText.includes('amzn')) {
    return 'amazon';
  }
  
  // Walmart detection
  if (lowerText.includes('walmart') || 
      lowerText.includes('great value') ||
      lowerText.includes('sam\'s choice')) {
    return 'walmart';
  }
  
  return 'unknown';
}

/**
 * Extract ASIN from text (Amazon product ID)
 * @param {string} text - Text to search
 * @returns {string|null} - ASIN if found
 */
export function extractASIN(text) {
  // ASIN pattern: B followed by 9 alphanumeric characters
  const asinPattern = /\b(B[0-9A-Z]{9})\b/g;
  const matches = text.match(asinPattern);
  
  return matches ? matches[0] : null;
}

/**
 * Extract price from text
 * @param {string} text - Text to search
 * @returns {number|null} - Price if found
 */
export function extractPrice(text) {
  // Price patterns: $X.XX, $X, XX.XX
  const pricePatterns = [
    /\$\s?(\d+\.\d{2})/g, // $19.99 or $ 19.99
    /\$\s?(\d+)/g, // $19
    /(\d+\.\d{2})\s?(?:USD|dollars?)/gi // 19.99 USD
  ];
  
  for (const pattern of pricePatterns) {
    const matches = text.match(pattern);
    if (matches && matches.length > 0) {
      // Extract numeric value
      const priceText = matches[0].replace(/[$\s,]/g, '');
      const price = parseFloat(priceText);
      
      // Validate price (between $0.01 and $999.99)
      if (price > 0 && price < 1000) {
        return price;
      }
    }
  }
  
  return null;
}

/**
 * Extract product name from text
 * This is a simplified version - real implementation would use NLP
 * @param {string} text - Full extracted text
 * @returns {string|null} - Product name
 */
export function extractProductName(text) {
  // Remove common noise words
  const lines = text.split('\n').filter(line => line.trim().length > 0);
  
  // Look for product title patterns
  // Usually the longest line or lines with brand names
  const filteredLines = lines.filter(line => {
    const lower = line.toLowerCase();
    // Skip lines that look like metadata
    return !lower.includes('add to cart') &&
           !lower.includes('buy now') &&
           !lower.includes('review') &&
           !lower.includes('rating') &&
           !lower.includes('shipping') &&
           line.length > 10; // At least 10 characters
  });
  
  if (filteredLines.length > 0) {
    // Return longest line as likely product name
    return filteredLines.reduce((a, b) => a.length > b.length ? a : b);
  }
  
  return null;
}

/**
 * Smart photo analysis - combines all extraction methods
 * @param {string} extractedText - All text from image
 * @returns {Object} - Structured product data
 */
export function analyzeProductPhoto(extractedText) {
  const store = detectStore(extractedText);
  const asin = extractASIN(extractedText);
  const price = extractPrice(extractedText);
  const productName = extractProductName(extractedText);
  
  return {
    store,
    asin,
    price,
    productName,
    confidence: calculateConfidence({ store, asin, price, productName }),
    rawText: extractedText
  };
}

/**
 * Calculate confidence score
 * @param {Object} data - Extracted data
 * @returns {string} - 'high', 'medium', or 'low'
 */
function calculateConfidence(data) {
  let score = 0;
  
  if (data.store !== 'unknown') score += 25;
  if (data.asin) score += 30;
  if (data.price) score += 25;
  if (data.productName && data.productName.length > 15) score += 20;
  
  if (score >= 70) return 'high';
  if (score >= 40) return 'medium';
  return 'low';
}

/**
 * Placeholder for future Google Cloud Vision API integration
 * @param {string} base64Image - Base64 encoded image
 * @returns {Promise<Object>} - Vision API response
 */
export async function callGoogleVisionAPI(base64Image) {
  // TODO: Integrate Google Cloud Vision API
  // This will require setting up API key in Firebase Functions
  
  // For now, return mock data
  // In production, this would be a Cloud Function call to keep API key secure
  
  /*
  Example structure when implementing:
  
  const response = await fetch('YOUR_CLOUD_FUNCTION_URL/analyzeImage', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ image: base64Image })
  });
  
  const data = await response.json();
  return data.textAnnotations[0].description; // Full text
  */
  
  throw new Error('Google Vision API not yet configured');
}

/**
 * Process uploaded image and extract product info
 * @param {File} imageFile - Uploaded image
 * @returns {Promise<Object>} - Product information
 */
export async function processProductImage(imageFile) {
  try {
    // For MVP, we'll use a smart client-side extraction
    // Later, integrate with Google Cloud Vision API via Cloud Function
    
    // Create image preview
    const imageUrl = URL.createObjectURL(imageFile);
    
    // Simulate processing
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // Return structure for manual entry with AI assistance
    return {
      success: true,
      imageUrl,
      needsManualEntry: true,
      suggestion: {
        message: "🤖 I can see your product image! Let me help you fill in the details.",
        nextStep: "Please type the product name you see, and I'll estimate the price."
      }
    };
  } catch (error) {
    console.error('Image processing error:', error);
    throw error;
  }
}


